import React, { useMemo, useRef } from 'react'
import filter from 'lodash/filter'
import find from 'lodash/find'
import { useToggle } from 'react-use'
import type { ReactNode } from 'react'
import { BusyButton } from '@ztechapps/webui.button'
import { BasePopover, Table } from '@ztechapps/webui.data-tools'
import { HorizontalStack, Icon, Link } from '@ztechapps/webui.util'
import { useDashboardContext } from '../Context'
import { ListRow } from './ListRow'

export type WidgetOption = {
  value: string
  check: boolean
}

export const WidgetOptions = ({ trigger = <Icon name={'gears'} aria-hidden="true" /> }: { trigger?: ReactNode }) => {
  const { setting, widgetNames, resolveWidget, onAddWidget, onRemoveWidget, onReset } = useDashboardContext()

  const [show, setShow] = useToggle(false)
  const target = useRef<HTMLDivElement>(null)

  const widgetItems = useMemo<WidgetOption[]>(() => {
    return filter(Object.values(widgetNames), (id) => !!resolveWidget(id)).map((id) => ({
      _id: id,
      value: id,
      check: !!find(setting.layout, (item) => item?.i === id),
    }))
  }, [setting.layout, widgetNames, resolveWidget])

  return (
    <HorizontalStack gap={2}>
      <div ref={target}>
        <Link onClick={() => setShow(true)} aria-label="Widgets">
          {trigger}
        </Link>
      </div>

      <BasePopover
        name="widget-option"
        show={show}
        setShow={setShow}
        target={target}
        header={
          <HorizontalStack className="justify-content-between">
            <div className="fw-bold">Widgets</div>
            <BusyButton
              text={'Reset'}
              onClick={async () => {
                await onReset()
                setShow(false)
              }}
              bsSize="sm"
            />
          </HorizontalStack>
        }
      >
        <Table
          dataArray={widgetItems}
          row={ListRow}
          onSelect={async (item: WidgetOption) => {
            if (item.check) await onRemoveWidget(item.value)
            else await onAddWidget(item.value)
          }}
        />
      </BasePopover>
    </HorizontalStack>
  )
}
